import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { isTaskOpen } from "@easyshop/shared";
import type { BoutiqueDetailDTO, BoutiqueStockDTO } from "@easyshop/shared";
import { Button, Card, Empty, ErrorState, Loading, SegPills, cx } from "@/components/ui";
import { PeriodBar } from "@/components/PeriodBar";
import { CaPanel, CountChips, KpiTiles, LEVEL_LABELS, LevelBadge, PCard, SearchField, SectionLink, StatRow, StatTile, stockLevel, type StockLevel } from "@/components/pc/Kit";
import { StockTable, levelOf, type StockTableRow } from "@/components/pc/StockTable";
import { PositionGroup, TopProductsGroup } from "@/components/Rankings";
import { IosGroup, IosRow, IosSection, IosSegmented } from "@/components/mobile/MobileUi";
import { SaleRow } from "@/components/sales/SaleRow";
import { WeekPlanning } from "@/routes/Planning";
import { PromosPage } from "@/routes/Promos";
import { mondayYmd } from "@/components/planning/utils";
import { Composer, ThreadMessages } from "@/components/messaging/Thread";
import { TaskCard } from "@/components/messaging/MessageBubble";
import { PageHeader, PageStack } from "@/layouts/DesktopShell";
import { useSession } from "@/hooks/useSession";
import { useUi } from "@/hooks/useUi";
import { ApiError, api } from "@/lib/api";
import {
  useBoutiqueArrivals,
  useBoutiqueConversationId,
  useBoutiqueOverview,
  useBoutiqueSales,
  useBoutiqueStock,
  useBoutiqueTasks,
  useStaffMessage,
} from "@/lib/queries";
import { dayMonth, eur, eur2, margin, num, relative } from "@/lib/format";

type TabKey = "apercu" | "stock" | "arrivages" | "ventes" | "planning" | "messages";

/** Onglets affichés ; « planning » et « messages » restent joignables par lien (notifications). */
const TABS: { key: TabKey; label: string }[] = [
  { key: "apercu", label: "Aperçu" },
  { key: "stock", label: "Stock" },
  { key: "arrivages", label: "Arrivages" },
  { key: "ventes", label: "Ventes" },
];

/**
 * Page boutique (/boutiques/:id, onglet dans ?tab=) ; aussi l'accueil d'un
 * gérant d'une seule boutique (`boutiqueId`).
 */
export function BoutiquePage({ boutiqueId }: { boutiqueId?: string } = {}) {
  const params0 = useParams<{ id: string }>();
  const id = boutiqueId ?? params0.id;
  const [params, setParams] = useSearchParams();
  const { isManagerView, can } = useSession();
  const q = useBoutiqueOverview(id);
  const stock = useBoutiqueStock(can("stock.read") ? id : undefined);
  const tabs = TABS.filter((t) => (t.key === "stock" ? can("stock.read") : t.key === "arrivages" ? can("arrivals.read") : t.key === "ventes" ? can("sales.read") : true));
  // « produits » : ancien nom de l'onglet Stock ; « taches » : fusionné avec les messages.
  const rawTab = params.get("tab");
  const tabParam = (rawTab === "produits" ? "stock" : rawTab === "taches" ? "messages" : rawTab) as TabKey | null;
  const hidden: TabKey[] = ["planning", "messages"];
  const tab: TabKey = tabParam && (tabs.some((t) => t.key === tabParam) || hidden.includes(tabParam)) ? tabParam : "apercu";
  const setTab = (t: TabKey) => {
    const next = new URLSearchParams(params);
    if (t === "apercu") next.delete("tab");
    else next.set("tab", t);
    setParams(next);
  };

  if (q.isLoading) return <Loading />;
  if (q.error || !q.data || !id) {
    const status = q.error instanceof ApiError ? q.error.status : 0;
    return (
      <div className="flex flex-col items-start gap-4">
        <ErrorState
          className="w-full"
          error={status === 404 || status === 403 ? new Error("Cette boutique est introuvable ou hors de votre périmètre.") : q.error}
        />
        <Link to="/" className="text-[15px] text-accent">
          ‹ Accueil
        </Link>
      </div>
    );
  }

  const d = q.data;
  const b = d.boutique;
  const franchise = b.type === "FRANCHISE";
  const refs = stock.data?.kpis.refs;

  return (
    <PageStack>
      <PageHeader
        title={b.city}
        badge={
          <>
            <span className={cx("rounded-[6px] px-[7px] py-[2px] text-[12px] font-semibold", !b.synced ? "bg-warn-bg text-warn" : franchise ? "bg-franchise-bg text-franchise" : "bg-positive-bg text-positive")}>
              {!b.synced ? "Non synchronisée" : franchise ? "Franchise" : "Intégrée"}
            </span>
            <PromoTodayPill promos={b.promosToday} onClick={() => setTab("planning")} />
          </>
        }
        subtitle={[
          franchise ? "Franchise" : "Succursale intégrée",
          boutiqueId && isManagerView ? "gérant" : refs != null ? `${num(refs)} références` : null,
          b.openingHours ? `ouvert ${b.openingHours}` : null,
        ]
          .filter(Boolean)
          .join(" · ")}
        right={
          <div className="w-[min(440px,100%)]">
            <IosSegmented options={tabs} value={tabs.some((t) => t.key === tab) ? tab : ("" as TabKey)} onChange={setTab} />
          </div>
        }
      />

      {(tab === "apercu" || tab === "ventes") && <PeriodBar />}
      {tab === "apercu" && <Overview d={d} onTab={setTab} />}
      {tab === "stock" && <StockTab id={id} />}
      {tab === "ventes" && <SalesTab id={id} />}
      {tab === "arrivages" && <ArrivalsTab id={id} />}
      {tab === "planning" && <PlanningTab id={id} />}
      {tab === "messages" && <MessagesTasksTab id={id} />}
    </PageStack>
  );
}

// --- Aperçu ---------------------------------------------------------------------

function Overview({ d, onTab }: { d: BoutiqueDetailDTO; onTab: (t: TabKey) => void }) {
  const b = d.boutique;
  const { can } = useSession();
  const { openProduct } = useUi();
  const stock = useBoutiqueStock(can("stock.read") ? b.id : undefined);
  const arrivals = useBoutiqueArrivals(can("arrivals.read") ? b.id : undefined);
  const watch = (stock.data?.products ?? [])
    .map((p) => ({ p, level: stockLevel(p.stock, p.coverageDays) }))
    .filter((x) => x.level === "rupture" || x.level === "faible")
    .sort((x, y) => (x.level === y.level ? x.p.stock - y.p.stock : x.level === "rupture" ? -1 : 1))
    .slice(0, 5);
  const lastArrivals = (arrivals.data?.months ?? []).flatMap((m) => m.items).slice(0, 3);
  const open = (p: { id: string | null; name: string }) => openProduct({ id: p.id, name: p.name, scope: "boutique", boutiqueId: b.id });

  return (
    <>
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
        <CaPanel label={`CA — ${d.period.title}`} ca={b.ca} delta={b.delta} prevCa={b.prevCa} columns={d.cmpColumns} chart={d.chart} />
        <KpiTiles
          columns={d.cmpColumns}
          items={[
            ["Tickets", num(b.tickets), (c) => c.tickets],
            ["Panier moyen", eur(b.panier), (c) => c.panier],
            ["Marge brute", margin(b.marge), (c) => c.marge],
            ["% objectif", b.objectivePct == null ? "—" : `${Math.round(b.objectivePct)} %`, (c) => c.objectif],
          ]}
        />
      </div>

      <div className="grid items-start gap-4 md:grid-cols-2 xl:grid-cols-3">
        <PositionGroup items={d.position} />
        <TopProductsGroup title={`Top produits — ${b.city}`} products={d.topProducts} ranked onSelect={open} />
        <div className="flex min-w-0 flex-col gap-[22px]">
          {can("stock.read") && (
            <IosSection title="Stock à surveiller" action={<SectionLink onClick={() => onTab("stock")}>Tout le stock</SectionLink>}>
              <IosGroup>
                {stock.isLoading && <div className="px-4 py-3 text-[15px] text-muted">Chargement…</div>}
                {stock.data && watch.length === 0 && <div className="px-4 py-3 text-[15px] text-muted">Rien à signaler.</div>}
                {watch.map(({ p, level }) => (
                  <IosRow
                    key={p.productId}
                    onClick={() => open(p)}
                    chevron={false}
                    right={
                      <span className="flex flex-shrink-0 items-center gap-2">
                        <span className="text-[13px] tabular text-muted">{num(p.stock)} u.</span>
                        <LevelBadge level={level} />
                      </span>
                    }
                  >
                    <span className="block truncate text-[15px]">{p.name}</span>
                  </IosRow>
                ))}
              </IosGroup>
            </IosSection>
          )}
          {can("arrivals.read") && lastArrivals.length > 0 && (
            <IosSection title="Derniers arrivages" action={<SectionLink onClick={() => onTab("arrivages")}>Tous</SectionLink>}>
              <IosGroup>
                {lastArrivals.map((a) => (
                  <IosRow key={a.id} onClick={() => onTab("arrivages")} right={<span className="flex-shrink-0 text-[14px] font-semibold tabular">{eur(a.amount)}</span>}>
                    <span className="block truncate text-[15px]">{a.supplier ?? "Sans fournisseur"}</span>
                    <span className="block text-[12px] text-muted">
                      {a.date ? dayMonth(a.date) : "—"} · {num(a.units)} u.
                    </span>
                  </IosRow>
                ))}
              </IosGroup>
            </IosSection>
          )}
        </div>
      </div>

      <StaffMessagePanel boutiqueId={b.id} />
    </>
  );
}

/** Pastille « Journée promo » à côté du nom de la boutique (rien s'il n'y a pas de promo). */
function PromoTodayPill({ promos, onClick }: { promos: BoutiqueDetailDTO["boutique"]["promosToday"]; onClick: () => void }) {
  if (promos.length === 0) return null;
  const label = promos.length === 1 ? promos[0].name : `${promos.length} promos`;
  return (
    <button
      type="button"
      onClick={onClick}
      title={promos.map((p) => p.name).join(", ")}
      className="inline-flex max-w-[260px] items-center gap-[6px] rounded-full bg-accent px-[10px] py-[3px] text-[11px] font-bold text-white hover:bg-accent-strong"
    >
      <span aria-hidden>🏷</span>
      <span className="truncate">Journée promo · {label}</span>
    </button>
  );
}

/** Rendu minimal du markdown des messages IA : **gras** uniquement. */
function RichText({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith("**") && p.endsWith("**") && p.length > 4 ? <strong key={i}>{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>
      )}
    </>
  );
}

function stripMarkdown(text: string) {
  return text
    .replace(/\*\*/g, "")
    .replace(/^#+\s*/gm, "")
    .replace(/^[-*•]\s+/gm, "")
    .trim();
}

/** Titre de tâche : première ligne / première phrase du message, sans markdown. */
function firstSentence(text: string) {
  const line = stripMarkdown(text).split("\n").find((l) => l.trim()) ?? "";
  const m = line.match(/^(.+?[.!?])(\s|$)/);
  const s = (m ? m[1] : line).trim();
  return s.length > 120 ? s.slice(0, 117).trimEnd() + "…" : s;
}

/** Bloc IA « Aujourd'hui en boutique » (message du jour pour l'équipe). */
function StaffMessagePanel({ boutiqueId }: { boutiqueId: string }) {
  const { can, isManagerView } = useSession();
  const { openCompose } = useUi();
  const qc = useQueryClient();
  const q = useStaffMessage(boutiqueId);
  const [regenerating, setRegenerating] = useState(false);
  const [regenError, setRegenError] = useState<string | null>(null);
  const canTask = can("tasks.create");

  const regenerate = async () => {
    setRegenerating(true);
    setRegenError(null);
    try {
      const res = await api.put<{ text: string; isOverride: boolean; date: string }>(`/boutiques/${boutiqueId}/staff-message`, {
        regenerate: true,
      });
      qc.setQueryData(["staff-message", boutiqueId], res);
      await qc.invalidateQueries({ queryKey: ["staff-message", boutiqueId] });
    } catch (e) {
      setRegenError(e instanceof Error ? e.message : "Génération impossible");
    } finally {
      setRegenerating(false);
    }
  };

  const text = q.data?.text ?? "";
  const busy = q.isLoading || regenerating;

  return (
    <div className="rounded-ios bg-accent-soft px-5 py-4">
      <div className="flex items-center gap-[10px]">
        <span className="rounded-[6px] bg-white px-[7px] py-[2px] text-[12px] font-bold text-accent">IA</span>
        <span className="text-[15px] font-semibold">Aujourd'hui en boutique</span>
        <div className="flex-1" />
        {canTask && (
          <button type="button" disabled={busy} onClick={() => void regenerate()} className="whitespace-nowrap text-[12px] font-semibold text-accent disabled:opacity-50">
            ↻ Régénérer
          </button>
        )}
      </div>
      <p className="mb-[14px] mt-3 whitespace-pre-line text-[14px] leading-[1.55] text-[oklch(0.22_0.02_150)]">
        {busy ? (
          <span className="text-muted">Génération du message du jour…</span>
        ) : q.error ? (
          <span className="text-muted">Message du jour indisponible ({q.error instanceof Error ? q.error.message : "erreur"}).</span>
        ) : (
          <RichText text={text} />
        )}
      </p>
      {regenError && <div className="mb-3 text-[12px] font-semibold text-danger">{regenError}</div>}
      {canTask && !isManagerView && text && !busy && (
        <button
          type="button"
          onClick={() => openCompose({ kind: "tache", boutiqueIds: [boutiqueId], title: firstSentence(text), description: stripMarkdown(text) })}
          className="inline-block rounded-[10px] bg-accent px-4 py-[9px] text-[14px] font-semibold text-white hover:bg-accent-strong"
        >
          Envoyer au gérant comme tâche
        </button>
      )}
    </div>
  );
}

// --- Stock -------------------------------------------------------------------------

type LevelFilter = "tous" | StockLevel;

/** Stock de la boutique : KPI, recherche, filtres par statut, tableau de tous les produits. */
function StockTab({ id }: { id: string }) {
  const q = useBoutiqueStock(id);
  const { can } = useSession();
  const { openProduct } = useUi();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<LevelFilter>("tous");
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rows: StockTableRow[] = useMemo(
    () =>
      (q.data?.products ?? []).map((p) => ({
        key: String(p.productId),
        id: p.id,
        name: p.name,
        category: p.category,
        stock: p.stock,
        sold30: p.sold30,
        coverageDays: p.coverageDays,
        price: p.price,
        supplyPrice: p.supplyPrice,
        promo: p.promo,
      })),
    [q.data]
  );

  const refresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const res = await api.post<BoutiqueStockDTO>(`/boutiques/${id}/stock/refresh`);
      qc.setQueryData(["stock", id], res);
      await qc.invalidateQueries({ queryKey: ["stock", id] });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Actualisation impossible");
    } finally {
      setRefreshing(false);
    }
  };

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorState error={q.error} />;
  const data = q.data!;
  const count = (l: StockLevel) => rows.filter((r) => levelOf(r) === l).length;
  const needle = normalize(search.trim());
  const shown = rows.filter((r) => (filter === "tous" || levelOf(r) === filter) && (!needle || normalize(`${r.name} ${r.category ?? ""}`).includes(needle)));

  return (
    <>
      <StatRow>
        <StatTile label="Références" value={num(data.kpis.refs)} />
        <StatTile label="Valeur du stock (achat)" value={eur(data.kpis.costValue)} />
        <StatTile label="Ruptures" value={num(count("rupture"))} tone="danger" />
        <StatTile label="Stock faible" value={num(count("faible"))} tone="warn" />
      </StatRow>
      <div className="flex flex-wrap items-center gap-3">
        <SearchField value={search} onChange={setSearch} className="w-[400px] max-w-full" />
        <CountChips
          value={filter}
          onChange={setFilter}
          options={[
            { key: "tous", label: "Tous", count: rows.length },
            ...(["rupture", "faible", "surstock", "ok"] as StockLevel[]).map((l) => ({ key: l, label: LEVEL_LABELS[l], count: count(l) })),
          ]}
        />
        <div className="flex-1" />
        <span className="text-[12.5px] text-muted">{data.updatedAt ? `Relevé ${relative(data.updatedAt)}` : "Stock jamais relevé"}</span>
        {can("sync.run") && (
          <button type="button" disabled={refreshing} onClick={() => void refresh()} className="text-[14px] text-accent disabled:opacity-50">
            {refreshing ? "Actualisation…" : "Actualiser"}
          </button>
        )}
      </div>
      {error && <ErrorState error={new Error(error)} />}
      <StockTable rows={shown} mode="boutique" onOpen={(r) => openProduct({ id: r.id, name: r.name, scope: "boutique", boutiqueId: id })} />
    </>
  );
}

function normalize(s: string) {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

// --- Ventes -----------------------------------------------------------------------

function SalesTab({ id }: { id: string }) {
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<string | null>(null);
  const q = useBoutiqueSales(id, page);
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorState error={q.error} />;
  const data = q.data!;
  const t = data.totals;
  return (
    <>
      <StatRow>
        <StatTile label="Tickets" value={num(data.total)} />
        <StatTile label="CA encaissé" value={eur(t.amount)} />
        <StatTile
          label="Remises accordées"
          value={eur(t.discount)}
          tone={t.discount > 0 ? "warn" : undefined}
          hint={`${num(t.discountedSales)} ticket${t.discountedSales > 1 ? "s" : ""} remisé${t.discountedSales > 1 ? "s" : ""}`}
        />
        <StatTile label="Bénéfice" value={eur(t.profit)} />
      </StatRow>
      <PCard className="px-2 py-[6px] sm:px-4">
        {data.sales.length === 0 && <Empty>Aucune vente sur la période.</Empty>}
        {data.sales.map((s, i) => (
          <SaleRow key={s.id} s={s} open={open === s.id} onToggle={() => setOpen((o) => (o === s.id ? null : s.id))} last={i === data.sales.length - 1} />
        ))}
      </PCard>
      {data.pages > 1 && (
        <div className="flex items-center justify-center gap-3 text-[12.5px]">
          <Button variant="outline" className="px-[14px] py-[6px] text-[12px]" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            ← Précédent
          </Button>
          <span className="text-muted">
            Page {data.page} / {data.pages} · {num(data.total)} tickets
          </span>
          <Button variant="outline" className="px-[14px] py-[6px] text-[12px]" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}>
            Suivant →
          </Button>
        </div>
      )}
    </>
  );
}

// --- Arrivages ----------------------------------------------------------------------

/** Arrivages de la boutique : KPI des 60 derniers jours, liste dépliable (clic sur un produit → fiche). */
function ArrivalsTab({ id }: { id: string }) {
  const q = useBoutiqueArrivals(id);
  const { openProduct } = useUi();
  const [openId, setOpenId] = useState<string | null>(null);
  const [shown, setShown] = useState(30);
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorState error={q.error} />;
  const all = q.data!.months.flatMap((m) => m.items);
  const since = Date.now() - 60 * 86_400_000;
  const recent = all.filter((a) => a.date && new Date(a.date).getTime() >= since);
  return (
    <>
      <StatRow cols={3}>
        <StatTile label="Arrivages (60 j)" value={num(recent.length)} />
        <StatTile label="Unités reçues" value={num(recent.reduce((s, a) => s + a.units, 0))} />
        <StatTile label="Valeur reçue (achat)" value={eur(recent.reduce((s, a) => s + a.amount, 0))} />
      </StatRow>
      {all.length === 0 ? (
        <PCard className="px-4 py-8 text-center text-[15px] text-muted">Aucun arrivage enregistré.</PCard>
      ) : (
        <PCard className="overflow-hidden">
          {all.slice(0, shown).map((a) => {
            const open = openId === a.id;
            return (
              <div key={a.id} className="border-b-[0.5px] border-ios-sep last:border-b-0">
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : a.id)}
                  aria-expanded={open}
                  className="flex min-h-[58px] w-full items-center gap-4 px-4 text-left hover:bg-[oklch(0.975_0.004_145)]"
                >
                  <span className="w-[64px] flex-shrink-0 text-[14px] text-muted">{a.date ? dayMonth(a.date) : "—"}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px]">{a.supplier ?? "Sans fournisseur"}</span>
                    <span className="block text-[12px] text-muted">
                      {num(a.lines.length)} produit{a.lines.length > 1 ? "s" : ""} · {num(a.units)} u.
                    </span>
                  </span>
                  <span className="flex-shrink-0 text-[15px] font-semibold tabular">{eur(a.amount)}</span>
                  <span aria-hidden className={cx("flex-shrink-0 text-[13px] text-ios-chevron transition-transform", open && "rotate-180")}>
                    ⌄
                  </span>
                </button>
                {open && (
                  <div className="mx-4 mb-3 rounded-[10px] bg-[oklch(0.97_0.004_145)] px-3 py-1">
                    {a.lines.length === 0 && <div className="py-2 text-[13px] text-muted">Détail des produits non disponible.</div>}
                    {a.lines.map((l, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => openProduct({ id: l.id, name: l.name, scope: "boutique", boutiqueId: id })}
                        className="flex w-full justify-between gap-3 border-b-[0.5px] border-ios-sep py-2 text-left text-[14px] last:border-b-0 hover:text-accent"
                      >
                        <span className="min-w-0 truncate">{l.name}</span>
                        <span className="flex-shrink-0 tabular text-muted">
                          {num(l.quantity)} u.{l.unitCost != null ? ` · ${eur2(l.unitCost)}` : ""}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {all.length > shown && (
            <button type="button" onClick={() => setShown((n) => n + 30)} className="w-full border-t-[0.5px] border-ios-sep py-3 text-[15px] text-accent">
              Voir plus ({num(all.length - shown)})
            </button>
          )}
        </PCard>
      )}
    </>
  );
}

// --- Planning & promos ---------------------------------------------------------------

/** Planning de la semaine (avec les promos du jour) ou calendrier des promos de la boutique. */
function PlanningTab({ id }: { id: string }) {
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const canPlanning = can("planning.read");
  const canPromos = can("campaigns.read");
  const view: "planning" | "promos" = !canPlanning || (canPromos && params.get("vue") === "promos") ? "promos" : "planning";
  const week = params.get("semaine") ?? mondayYmd();
  const set = (k: string, v: string | null) => {
    const next = new URLSearchParams(params);
    if (v == null) next.delete(k);
    else next.set(k, v);
    setParams(next, { replace: true });
  };
  return (
    <div className="flex flex-col gap-4">
      {canPlanning && canPromos && (
        <SegPills
          options={[
            { key: "planning", label: "Planning" },
            { key: "promos", label: "Promos" },
          ]}
          value={view}
          onChange={(v) => set("vue", v === "promos" ? "promos" : null)}
        />
      )}
      {view === "planning" ? <WeekPlanning boutiqueId={id} from={week} onWeek={(w) => set("semaine", w)} /> : <PromosPage boutiqueId={id} />}
    </div>
  );
}

// --- Messages ------------------------------------------------------------------------

/** Messages et tâches ensemble : le fil (les tâches y apparaissent aussi) et, à côté, la liste des tâches. */
function MessagesTasksTab({ id }: { id: string }) {
  const q = useBoutiqueConversationId(id);
  const tasksQ = useBoutiqueTasks(id);
  const tasks = [...(tasksQ.data ?? [])].sort((a, b) => Number(!isTaskOpen(a.status)) - Number(!isTaskOpen(b.status)));
  return (
    <div className="flex flex-wrap items-start gap-4">
      <Card className="flex min-w-0 flex-[1_1_420px] flex-col gap-[14px] lg:max-w-[640px]">
        {q.isLoading ? (
          <Loading />
        ) : q.error ? (
          <ErrorState error={q.error} />
        ) : q.data ? (
          <>
            <div className="max-h-[560px] overflow-auto rounded-card-sm bg-page px-3 py-4">
              <ThreadMessages conversationId={q.data.id} />
            </div>
            <Composer conversationId={q.data.id} className="border-t border-line-soft pt-[14px]" />
          </>
        ) : null}
      </Card>
      <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-[10px]">
        <div className="text-[15px] font-bold">
          Tâches <span className="text-[12.5px] font-semibold text-muted-2">· {tasks.filter((a) => isTaskOpen(a.status)).length} à traiter</span>
        </div>
        {tasksQ.isLoading ? (
          <Loading />
        ) : tasksQ.error ? (
          <ErrorState error={tasksQ.error} />
        ) : tasks.length === 0 ? (
          <Empty>Aucune tâche pour cette boutique.</Empty>
        ) : (
          <div className="flex max-h-[640px] flex-col gap-[10px] overflow-auto">
            {tasks.map((a) => (
              <TaskCard key={a.id} a={a} compact />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
